/* eslint-disable max-classes-per-file */
/**
 * Logger to handler error message logging in the browser
 */
import _get from 'lodash/get'
import _findIndex from 'lodash/findIndex'
import _orderBy from 'lodash/orderBy'
import _filter from 'lodash/filter'
import _size from 'lodash/size'
import _takeRight from 'lodash/takeRight'
import _isObject from 'lodash/isObject'
import { get as getConfig } from 'config'
import {
  createLogger, ConsoleFormattedStream, ERROR, DEBUG,
} from 'browser-bunyan'
import { getName, getVersion, getEnv } from './common'
import { push as pushNewRelic } from '../browser/new-relic'

/**
 * @typedef NewRelicLoggerStreamError
 * @type {Object}
 * @property {Number} lastSeenTimestamp The last timestamp the error was seen
 * @property {Number} nextReportTimestamp The next timestamp the error can be reported
 * @property {Error} err The error instance
 */

/** @type {Number} The maximum number of errors to track */
const errorsMax = 50

/** @type {NewRelicLoggerStreamError[]} Error aggregator singleton */
let _errors = []

/** @type {Number} How often should a unique error be reported */
const errorReportFrequencyMilliseconds = 60000

/**
 * Write a log record to NewRelic with rate limiting
 * @param {Object} record A log record
 */
function writeLogRecordToNewRelic (record = {}) {
  const { err } = record
  // Only write errors to NewRelic
  if (!err) {
    return
  }
  const existingErrorIndex = _findIndex(_errors, (error) => {
    const { err: seenErr } = error
    return seenErr.message === err.message && seenErr.stack === err.stack
  })
  const existingError = existingErrorIndex >= 0 ? _errors[existingErrorIndex] : undefined
  // If the error has already been seen and we have not passed the report time do not send
  const reportError = !existingError || !existingError.nextReportTimestamp > Date.now()
  // Update the last seen timestamp if error already exists
  if (existingErrorIndex >= 0) {
    _errors[existingErrorIndex].lastSeenTimestamp = Date.now()
  } else {
    // Keep the error to rate limit reporting
    _errors.push({
      lastSeenTimestamp: Date.now(),
      nextReportTimestamp: Date.now() + errorReportFrequencyMilliseconds,
      err,
    })
  }
  if (reportError) {
    pushNewRelic((newrelic) => {
      newrelic.noticeError(err)
    })
  }
  // Only keep errors that haven't passed the next report timestamp
  _errors = _filter(_errors, (error) => error.nextReportTimestamp > Date.now())
  // Check if we are tracking too many errors
  if (_size(_errors) > errorsMax) {
    // Drop the oldest errors
    _errors = _orderBy(_errors, [(error) => error.lastSeenTimestamp], ['ASC'])
    _errors = _takeRight(_errors, errorsMax)
  }
}

/** map logged objects message property to msg to fix buyan logging */
function writeLogRecordToObjectLoggerStream (record) {
  if (_isObject(record) && record.message && !record.msg) {
    // eslint-disable-next-line no-param-reassign
    record.msg = record.message
  }
}

/** Object logger to fix missing msg property using other object properties */
class ObjectLoggerStream {
  // Map class method to function since there is no use of this
  write = writeLogRecordToObjectLoggerStream
}

/** NewRelic log buyan stream with rate limiting */
class NewRelicLoggerStream {
  // Map class method to function since there is no use of this
  write = writeLogRecordToNewRelic
}

/**
 * Get a logger for the browser
 */
export function getLogger (options = {}) {
  const {
    log: {
      browser: logConfig = {},
    } = {},
  } = getConfig()
  const {
    name = getName(),
    version = getVersion(),
    env = getEnv(),
    streams = [],
    level = _get(logConfig, 'level') || DEBUG,
    outputMode = _get(logConfig, 'outputMode') || 'json',
  } = options
  const loggerConfig = {
    name,
    version,
    env,
    streams,
  }
  loggerConfig.streams.push({
    level,
    stream: new ObjectLoggerStream(),
  })
  switch (outputMode) {
    case 'short':
      loggerConfig.streams.push({
        level,
        stream: new NewRelicLoggerStream(),
      })
      loggerConfig.streams.push({
        level,
        stream: new ConsoleFormattedStream(),
      })
      break
    case 'json':
      loggerConfig.streams.push({
        level: ERROR,
        stream: new NewRelicLoggerStream(),
      })
      break
    case 'none':
      break
    default:
      break
  }
  return createLogger(loggerConfig)
}

export default {
  getLogger,
}
